import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TextField,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Snackbar,
} from '@mui/material'
import {
  Edit as EditIcon,
  Visibility as PreviewIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { emailService, EmailTemplateListItem, EmailTemplate } from '../services/emailService'

// カテゴリカラーマッピング（配色ルール: warning/infoは使用禁止、secondary に置換）
const categoryColors: Record<string, 'primary' | 'secondary' | 'default'> = {
  auth: 'primary',
  notification: 'primary',
  reminder: 'secondary',
  general: 'secondary',
}

const EmailTemplateManagePage: React.FC = () => {
  const { t, i18n } = useTranslation('emailTemplate')
  const { user } = useAuth()
  const [templates, setTemplates] = useState<EmailTemplateListItem[]>([])
  const [groupedTemplates, setGroupedTemplates] = useState<Record<string, EmailTemplateListItem[]>>(
    {}
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // プレビュー関連
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewContent, setPreviewContent] = useState<{
    subject: string
    html: string
    text?: string
  } | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  // 編集関連
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    subject: '',
    htmlBody: '',
    textBody: '',
    isActive: true,
  })
  const [saving, setSaving] = useState(false)
  const [editorTab, setEditorTab] = useState(0)

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await emailService.getTemplates()
      setTemplates(response.templates)
      setGroupedTemplates(response.groupedByCategory)
    } catch (err) {
      console.error('Failed to fetch templates:', err)
      setError(t('messages.loadError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  const handlePreview = async (templateKey: string) => {
    try {
      setPreviewLoading(true)
      setError(null)
      const preview = await emailService.previewTemplate(templateKey, {
        userName: t('sampleData.userName'),
        userEmail: 'test@garaponapps.com',
        testTitle: t('sampleData.testTitle'),
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        score: 85,
        passed: true,
        daysLeft: 3,
        temporaryPassword: 'TempPass123!',
        newPassword: 'NewPass456!',
        companyName: t('sampleData.companyName'),
      })

      setPreviewContent({
        subject: preview.subject,
        html: preview.html,
        text: preview.text,
      })
      setPreviewOpen(true)
    } catch (err) {
      console.error('Preview failed:', err)
      setError(t('messages.previewError'))
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleEdit = async (templateKey: string) => {
    try {
      setError(null)
      const template = await emailService.getTemplateDetails(templateKey)
      setEditingTemplate(template)
      setEditForm({
        name: template.name,
        description: template.description || '',
        subject: template.subject,
        htmlBody: template.htmlBody,
        textBody: template.textBody || '',
        isActive: template.isActive,
      })
      setEditorOpen(true)
    } catch (err) {
      console.error('Failed to load template:', err)
      setError(t('messages.loadDetailError'))
    }
  }

  const handleSave = async () => {
    if (!editingTemplate) return

    try {
      setSaving(true)
      setError(null)

      await emailService.saveTemplate(editingTemplate.templateKey, {
        name: editForm.name,
        description: editForm.description,
        subject: editForm.subject,
        htmlBody: editForm.htmlBody,
        textBody: editForm.textBody,
        isActive: editForm.isActive,
      })

      setSuccessMessage(t('messages.saveSuccess'))
      setEditorOpen(false)
      setEditingTemplate(null)
      await fetchTemplates()
    } catch (err) {
      console.error('Save failed:', err)
      setError(t('messages.saveError'))
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  if (!user || (user.role !== 'SUPER_ADMIN' && user.role !== 'CORPORATE_ADMIN')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('messages.noPermission')}</Alert>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* タイトル行 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('templateCount', { count: templates.length })}
        </Typography>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* カテゴリ別テンプレート一覧 */}
      {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
        <Box key={category} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={t(`categories.${category}`) || category}
              color={categoryColors[category] || 'default'}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              ({categoryTemplates.length})
            </Typography>
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
              gap: 3,
            }}
          >
            {categoryTemplates.map((template) => (
              <Card
                key={template.templateKey}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s',
                  opacity: template.isActive ? 1 : 0.6,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack spacing={2}>
                    {/* テンプレート名とステータス */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography variant="h6" component="h3" sx={{ flex: 1 }}>
                        {template.name}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {template.isSystem && (
                          <Chip label={t('template.system')} size="small" variant="outlined" />
                        )}
                        {!template.isActive && (
                          <Chip label={t('template.inactive')} size="small" color="error" />
                        )}
                      </Stack>
                    </Box>

                    {/* 説明 */}
                    {template.description && (
                      <Typography variant="body2" color="text.secondary">
                        {template.description.length > 80
                          ? `${template.description.substring(0, 80)}...`
                          : template.description}
                      </Typography>
                    )}

                    {/* 変数 */}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('template.variables')}:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {template.variables.slice(0, 4).map((variable) => (
                          <Chip
                            key={variable}
                            label={`{{${variable}}}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        ))}
                        {template.variables.length > 4 && (
                          <Chip
                            label={t('template.moreVariables', {
                              count: template.variables.length - 4,
                            })}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* 最終更新日 */}
                    <Typography variant="caption" color="text.secondary">
                      {t('template.lastUpdated', {
                        date: new Date(template.updatedAt).toLocaleDateString(
                          i18n.language === 'ja' ? 'ja-JP' : 'en-US'
                        ),
                      })}
                    </Typography>
                  </Stack>
                </CardContent>

                {/* アクションボタン */}
                <Box sx={{ p: 2, pt: 0 }}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title={t('actions.preview')}>
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(template.templateKey)}
                        color="primary"
                        disabled={previewLoading}
                      >
                        <PreviewIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t('actions.edit')}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(template.templateKey)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Card>
            ))}
          </Box>
        </Box>
      ))}

      {/* プレビューダイアログ */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('preview.title')}</DialogTitle>
        <DialogContent>
          {previewContent && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('preview.subject')}:
                </Typography>
                <Typography variant="body1">{previewContent.subject}</Typography>
              </Box>
              <Tabs value={editorTab} onChange={(_, v) => setEditorTab(v)}>
                <Tab label={t('preview.htmlTab')} />
                <Tab label={t('preview.textTab')} />
              </Tabs>
              {editorTab === 0 && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    maxHeight: 400,
                    overflow: 'auto',
                    bgcolor: 'background.paper',
                  }}
                  dangerouslySetInnerHTML={{ __html: previewContent.html }}
                />
              )}
              {editorTab === 1 && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    maxHeight: 400,
                    overflow: 'auto',
                    bgcolor: 'grey.100',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                  }}
                >
                  {previewContent.text || t('preview.noText')}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>{t('common:buttons.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6">
            {t('editor.title')}: {editingTemplate?.name}
          </Typography>
          <IconButton onClick={() => setEditorOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* 基本情報 */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label={t('editor.name')}
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                  />
                }
                label={t('template.active')}
              />
            </Box>

            <TextField
              label={t('editor.description')}
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label={t('editor.subject')}
              value={editForm.subject}
              onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
              fullWidth
              helperText={t('editor.subjectHint')}
            />

            {/* 利用可能な変数 */}
            {editingTemplate && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('template.variables')}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {editingTemplate.variables.map((variable) => (
                    <Chip
                      key={variable}
                      label={`{{${variable}}}`}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        navigator.clipboard.writeText(`{{${variable}}}`)
                        setSuccessMessage(
                          t('editor.variableCopied', { variable: `{{${variable}}}` })
                        )
                      }}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* HTML本文 */}
            <TextField
              label={t('editor.htmlBody')}
              value={editForm.htmlBody}
              onChange={(e) => setEditForm({ ...editForm, htmlBody: e.target.value })}
              fullWidth
              multiline
              rows={12}
              sx={{ fontFamily: 'monospace' }}
            />

            {/* テキスト本文 */}
            <TextField
              label={t('editor.textBody')}
              value={editForm.textBody}
              onChange={(e) => setEditForm({ ...editForm, textBody: e.target.value })}
              fullWidth
              multiline
              rows={6}
              sx={{ fontFamily: 'monospace' }}
              helperText={t('editor.textBodyHint')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>{t('common:buttons.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? t('common:buttons.saving') : t('common:buttons.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功メッセージ */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Box>
  )
}

export default EmailTemplateManagePage
